import React, { useCallback, useEffect, useState } from 'react';
import Loading from '../shared/Loading/Loading';
import Button from '../shared/Button/Button';
import StatusBadge from '../shared/StatusBadge/StatusBadge';
import {
  FormGroup,
  FormLabel,
  FormInput,
  FormSelect,
  ErrorMessage,
} from '../shared/Form/Form.styles';
import { Panel, IvaTag } from '../../pages/sales/Sales.styles';
import { FilterBar, PresetRow, Pager, EmptyBox } from '../../pages/sales/SalesList.styles';
import { ItemList, ItemCard, Chip, StatsGrid, LinkButton } from './promoters.styles';
import { promoterSaleStatus, saleSourceLabel, kindLabel } from './promoterLabels';
import { formatMoney } from '../../utils/money';
import { formatPercent } from '../../utils/commission';
import { formatCalendarDate, formatTimestamp, shiftCalendarDate, todayInMexico } from '../../utils/dates';

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todas' },
  { value: 'valid', label: 'Vigentes' },
  { value: 'pending', label: 'Pendientes' },
  { value: 'cancelled', label: 'Canceladas' },
];

const SOURCE_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'online', label: 'En línea' },
  { value: 'in_person', label: 'En persona' },
];

const commissionLabel = {
  promoter: 'Mi comisión',
  empresa: 'Comisión a pagar',
  admin: 'Comisión',
};

// Ventas atribuidas a promotores. Una sola vista para los tres roles:
//   viewer 'promoter' : sus ventas (con la empresa que las vendió)
//   viewer 'empresa'  : las ventas de sus promotores (+ resumen por promotor)
//   viewer 'admin'    : todas (con empresa y promotor)
// `load(params)` devuelve la respuesta del listado; `onError(err)` devuelve true
// si ya manejó el error (sesión inválida, estado del promotor...).
const PromoterSalesView = ({ viewer, load, onError, empresaOptions = [], promoterOptions = [] }) => {
  const today = todayInMexico();
  const [filters, setFilters] = useState({ from: '', to: '', status: 'all', source: '', empresaId: '', promoterId: '' });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const rangeInvalid = filters.from && filters.to && filters.from > filters.to;

  const fetchPage = useCallback(async () => {
    if (rangeInvalid) return;
    setLoading(true);
    setError('');
    try {
      const result = await load({
        from: filters.from,
        to: filters.to,
        status: filters.status,
        source: filters.source,
        empresaId: filters.empresaId,
        promoterId: filters.promoterId,
        page,
        limit: PAGE_SIZE,
      });
      setData(result);
    } catch (err) {
      if (onError && onError(err)) return;
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [load, onError, filters, page, rangeInvalid]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  const setFilter = (key) => (event) => {
    setPage(1);
    setFilters({ ...filters, [key]: event.target.value });
  };

  const applyPreset = (from, to) => {
    setPage(1);
    setFilters({ ...filters, from, to });
  };

  const preset = (() => {
    if (!filters.from && !filters.to) return 'all';
    if (filters.from === today && filters.to === today) return 'today';
    if (filters.to === today && filters.from === shiftCalendarDate(today, -6)) return 'week';
    if (filters.to === today && filters.from === shiftCalendarDate(today, -29)) return 'month';
    return '';
  })();

  const sales = (data && data.sales) || [];
  const summary = data && data.summary;
  const byPromoter = (data && data.byPromoter) || [];
  const totalPages = (data && data.totalPages) || 1;

  return (
    <>
      <Panel>
        <h2>Filtros</h2>
        <PresetRow role="group" aria-label="Fechas rápidas (fecha de venta)">
          <button type="button" aria-pressed={preset === 'today'} onClick={() => applyPreset(today, today)}>Hoy</button>
          <button type="button" aria-pressed={preset === 'week'} onClick={() => applyPreset(shiftCalendarDate(today, -6), today)}>Últimos 7 días</button>
          <button type="button" aria-pressed={preset === 'month'} onClick={() => applyPreset(shiftCalendarDate(today, -29), today)}>Últimos 30 días</button>
          <button type="button" aria-pressed={preset === 'all'} onClick={() => applyPreset('', '')}>Todas las fechas</button>
        </PresetRow>
        <FilterBar>
          <FormGroup>
            <FormLabel htmlFor="ps-from">Venta desde</FormLabel>
            <FormInput id="ps-from" type="date" value={filters.from} onChange={setFilter('from')} />
          </FormGroup>
          <FormGroup>
            <FormLabel htmlFor="ps-to">Venta hasta</FormLabel>
            <FormInput id="ps-to" type="date" value={filters.to} onChange={setFilter('to')} />
          </FormGroup>
          <FormGroup>
            <FormLabel htmlFor="ps-status">Estado</FormLabel>
            <FormSelect id="ps-status" value={filters.status} onChange={setFilter('status')}>
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </FormSelect>
          </FormGroup>
          <FormGroup>
            <FormLabel htmlFor="ps-source">Origen</FormLabel>
            <FormSelect id="ps-source" value={filters.source} onChange={setFilter('source')}>
              {SOURCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </FormSelect>
          </FormGroup>
          {viewer !== 'empresa' && empresaOptions.length > 0 && (
            <FormGroup>
              <FormLabel htmlFor="ps-empresa">Empresa</FormLabel>
              <FormSelect id="ps-empresa" value={filters.empresaId} onChange={setFilter('empresaId')}>
                <option value="">Todas</option>
                {empresaOptions.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </FormSelect>
            </FormGroup>
          )}
          {viewer !== 'promoter' && promoterOptions.length > 0 && (
            <FormGroup>
              <FormLabel htmlFor="ps-promoter">Promotor</FormLabel>
              <FormSelect id="ps-promoter" value={filters.promoterId} onChange={setFilter('promoterId')}>
                <option value="">Todos</option>
                {promoterOptions.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </FormSelect>
            </FormGroup>
          )}
        </FilterBar>
        {rangeInvalid && <ErrorMessage role="alert">La fecha inicial no puede ser posterior a la final.</ErrorMessage>}
      </Panel>

      <Panel>
        <h2>Resumen</h2>
        {summary ? (
          <StatsGrid>
            <div className="stat"><div className="value">{summary.sales}</div><div className="label">Ventas vigentes</div></div>
            <div className="stat">
              <div className="value">{formatMoney(summary.listAmount)}</div>
              <div className="label">Monto de lista <IvaTag>IVA incluido</IvaTag></div>
            </div>
            <div className="stat"><div className="value">{formatMoney(summary.commissionAmount)}</div><div className="label">{commissionLabel[viewer]}</div></div>
            <p className="caption">
              Cuenta solo las ventas <strong>vigentes</strong> de los filtros. Las fechas son las de la <strong>venta</strong>, en hora de México.
            </p>
          </StatsGrid>
        ) : (
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>{loading ? 'Calculando…' : 'Sin datos.'}</p>
        )}
      </Panel>

      {viewer !== 'promoter' && byPromoter.length > 0 && (
        <Panel>
          <h2>Ventas por promotor</h2>
          <ItemList>
            {byPromoter.map((row) => (
              <ItemCard key={row.promoter.id}>
                <div className="head">
                  <span className="title">{row.promoter.name}</span>
                  <Chip>{kindLabel(row.promoter.kind)}</Chip>
                </div>
                <div className="money">
                  <span><strong>{row.sales}</strong> {row.sales === 1 ? 'venta' : 'ventas'}</span>
                  <span>Lista: <strong>{formatMoney(row.listAmount)}</strong> <IvaTag>IVA incluido</IvaTag></span>
                  <span>{commissionLabel[viewer]}: <strong>{formatMoney(row.commissionAmount)}</strong></span>
                </div>
              </ItemCard>
            ))}
          </ItemList>
        </Panel>
      )}

      {error && (
        <ErrorMessage role="alert">
          {error} <LinkButton type="button" onClick={fetchPage}>Reintentar</LinkButton>
        </ErrorMessage>
      )}

      {loading && !data ? (
        <Loading text="Cargando ventas..." />
      ) : sales.length === 0 && !error ? (
        <EmptyBox>
          <h3>No hay ventas con estos filtros</h3>
          <p>
            {viewer === 'promoter'
              ? 'Cuando alguien reserve con tu enlace, la venta aparecerá aquí.'
              : 'Cambia las fechas o el estado para ver otras.'}
          </p>
        </EmptyBox>
      ) : (
        <>
          <ItemList aria-busy={loading}>
            {sales.map((sale) => {
              const state = promoterSaleStatus(sale.status);
              return (
                <ItemCard key={sale.id} data-muted={sale.status === 'cancelled'}>
                  <div className="head">
                    <span className="title">{sale.activityTitle}</span>
                    <StatusBadge status={state.status}>{state.label}</StatusBadge>
                  </div>
                  <div className="meta">
                    Vendida el {formatCalendarDate(sale.saleDate)} ({formatTimestamp(sale.soldAt)}) · actividad del {formatCalendarDate(sale.activityDate)}
                    {' · '}{sale.quantity} {sale.quantity === 1 ? 'lugar' : 'lugares'}
                    {sale.customerName ? ` · ${sale.customerName}` : ''}
                  </div>
                  <div className="meta">
                    <Chip>{saleSourceLabel(sale.source)}{sale.source === 'in_person' && sale.ticketNumber ? ` · ticket #${sale.ticketNumber}` : ''}</Chip>
                    {viewer !== 'empresa' && sale.empresa && <> Empresa: {sale.empresa.businessName}.</>}
                    {viewer !== 'promoter' && sale.promoter && <> Promotor: {sale.promoter.name} ({kindLabel(sale.promoter.kind)}).</>}
                  </div>
                  <div className="money">
                    <span>Venta: <strong>{formatMoney(sale.listAmount)}</strong> <IvaTag>IVA incluido</IvaTag></span>
                    <span>Comisión {formatPercent(sale.commissionPercent)}: <strong>{formatMoney(sale.commissionAmount)}</strong></span>
                  </div>
                  {(sale.cutId || sale.adjustmentCutId) && (
                    <div className="meta">
                      {sale.cutId ? `Incluida en el corte #${sale.cutId}.` : ''}
                      {sale.adjustmentCutId ? ` Cancelada después del corte: se descuenta en el corte #${sale.adjustmentCutId}.` : ''}
                    </div>
                  )}
                </ItemCard>
              );
            })}
          </ItemList>

          {totalPages > 1 && (
            <Pager>
              <Button size="small" variant="ghost" disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}>
                ← Anteriores
              </Button>
              <span>Página {page} de {totalPages} · {data.total} ventas</span>
              <Button size="small" variant="ghost" disabled={page >= totalPages || loading} onClick={() => setPage(page + 1)}>
                Siguientes →
              </Button>
            </Pager>
          )}
        </>
      )}
    </>
  );
};

export default PromoterSalesView;
